import { useState } from 'react'

import { Coupon } from '@/api/affiliate/types/Coupon'
import { useCurrentUser } from '@/api/auth/queries/useCurrentUser'
import { useCheckCoupon } from '@/api/payments/mutations/useCheckCoupon'
import { CheckCouponResponseData } from '@/api/payments/types/CheckCouponResponse'
import { UserPkg } from '@/api/user-package/types/UserPkg'
import PkgUniteIcon from '@/components/icons/pkg-unite.svg?react'
import { TextField } from '@/components/TextField'
import { Button } from '@/components/ui/button'

interface Props {
  onApplyCoupon: (coupon: CheckCouponResponseData) => void
  pkg: UserPkg
  uniteFee: number
  priceInILS: number
  shippingMethod: string
  onError?: (message: string) => void
  coupon: Coupon | null
}

export const CouponInput = ({ onApplyCoupon, pkg, uniteFee, priceInILS, shippingMethod, onError, coupon }: Props) => {
  const checkCouponMutation = useCheckCoupon()
  const { data: loggedUser } = useCurrentUser()
  const [value, setValue] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const handleApplyCoupon = async () => {
    if (!value.trim()) {
      setErrorMsg('נא למלא קוד קופון')
      return
    }

    setErrorMsg('')

    try {
      const res = await checkCouponMutation.mutateAsync({
        code: value,
        userId: loggedUser!.Username,
        priceInILS,
        shippingMethod,
        contents: pkg.contents,
        brand: pkg.brand,
        uniteFee,
        delayChargePrice: pkg.delayChargePrice ?? 0,
        warehouse: pkg.warehouse,
      })

      if (!res.errorCode) {
        onApplyCoupon(res.data)
        return
      }

      switch (res.errorCode) {
        case 1:
          setErrorMsg('הקופון לא נמצא')
          break
        case 2:
          setErrorMsg('קופון אזל :(')
          break
        case 3:
          setErrorMsg('קופון פג תוקף :(')
          break
        case 4:
          setErrorMsg('קופון לא זמין :(')
          break
        case 5:
        case 6:
          setErrorMsg('השתמשת כבר בקופון זה')
          break
        case 7:
          setErrorMsg(`לקופון זה יש מגבלת מינימום של ${res.data.minPayment} ש"ח`)
          break
        case 8:
          setErrorMsg('קופון זה לא תקף לסוג המשלוח שבחרת')
          break
        case 9:
          setErrorMsg('קופון זה לא תקף לקטגורית מוצרים זו')
          break
        case 10:
          setErrorMsg('קופון זה לא תקף למדינה זו')
          break
        default:
          setErrorMsg('לא ניתן לטפל בבקשה כעת')
      }
    } catch (error) {
      console.error('onSubmitCouponCode - err', error)
      const errorMessage = 'תקלה בשרת! אנא נסה שנית או פנה לשירות הלקוחות'
      setErrorMsg(errorMessage)
      if (onError) {
        onError(errorMessage)
      }
    }
  }

  return (
    <div className='space-y-3'>
      <div className='flex h-[70px] items-start gap-2'>
        <TextField
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder='הזן קוד קופון'
          className='grow'
          autoCapitalize='off'
          success={!!coupon}
          error={!!errorMsg}
          supportingText={errorMsg || (coupon ? 'הקופון הוזן בהצלחה' : '')}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              handleApplyCoupon()
            }
          }}
        />
        <Button
          disabled={checkCouponMutation.isPending}
          loading={checkCouponMutation.isPending}
          onClick={handleApplyCoupon}
          icon={<PkgUniteIcon className='size-6' />}
          variant='outline'
          className='h-12 px-4'
        >
          ממש
        </Button>
      </div>
    </div>
  )
}
